import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Section, Loading, KPITile, DataTable, StatusPill } from "../components/Common";
import { CheckCircle2, XCircle, Clock, HandMetal, Save, Sparkles, AlertTriangle, ClipboardCheck, Bell, BedDouble, Bus } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { v: "present", label: "Present", cls: "bg-emerald-600" },
  { v: "late", label: "Late", cls: "bg-amber-500" },
  { v: "absent", label: "Absent", cls: "bg-rose-600" },
  { v: "excused", label: "Excused", cls: "bg-slate-500" },
  { v: "sick", label: "Sick", cls: "bg-purple-600" },
  { v: "activity", label: "Activity", cls: "bg-sky-600" },
];

const BOARDING_STATUSES = [
  { v: "in_house", label: "In House", cls: "bg-emerald-600" },
  { v: "signed_out", label: "Signed Out", cls: "bg-amber-500" },
  { v: "leave", label: "Leave", cls: "bg-slate-500" },
  { v: "absent", label: "Absent", cls: "bg-rose-600" },
];

const TRANSPORT_STATUSES = [
  { v: "boarded", label: "Boarded", cls: "bg-emerald-600" },
  { v: "missed", label: "Missed", cls: "bg-rose-600" },
  { v: "own_transport", label: "Own", cls: "bg-slate-500" },
];

function RosterRow({ s, statuses, onMark, testPrefix }) {
  return (
    <div className="hl-card p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`${testPrefix}-row-${s.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800 text-sm truncate">{s.first_name} {s.last_name}</div>
        <div className="text-[11px] text-slate-500 flex items-center gap-2">
          <span className="mono">{s.admission_no}</span>
          {s.house && <span>· {s.house}</span>}
          {s.year_group && <span>· {s.year_group}</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {statuses.map((st) => (
          <button
            key={st.v}
            onClick={() => onMark(s.id, st.v)}
            data-testid={`${testPrefix}-mark-${s.id}-${st.v}`}
            className={`text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 rounded-md ${st.cls} ${s.status === st.v ? "ring-2 ring-offset-1 ring-[color:var(--hl-navy)]" : "opacity-70 hover:opacity-100"}`}
          >
            {st.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Attendance() {
  const [tab, setTab] = useState("rollcall");
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [roster, setRoster] = useState([]);
  const [boarders, setBoarders] = useState([]);
  const [transport, setTransport] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [briefing, setBriefing] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.classes().then(({ data }) => { setClasses(data); if (data.length && !classId) setClassId(data[0].id); });
    api.attendanceAnalytics().then(({ data }) => setAnalytics(data));
  }, []);

  useEffect(() => {
    if (!classId) return;
    api.attendanceRoster(classId, date).then(({ data }) => setRoster(data.students || []));
  }, [classId, date]);

  useEffect(() => {
    if (tab === "boarding") api.boardingRoll({ date }).then(({ data }) => setBoarders(data.boarders || []));
    if (tab === "transport") api.transportManifest().then(({ data }) => setTransport(data.routes || []));
  }, [tab, date]);

  const mark = (id, status) => setRoster((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
  const markBoarder = (id, status) => setBoarders((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
  const markTransport = (routeIdx, id, status) => setTransport((prev) => prev.map((r, i) => i === routeIdx ? {
    ...r, manifest: r.manifest.map((s) => s.id === id ? { ...s, status } : s)
  } : r));

  const saveClassRoll = async () => {
    setBusy(true);
    try {
      const marked = roster.filter((s) => s.status);
      const entries = marked.map((s) => ({ date, class_id: classId, student_id: s.id, status: s.status }));
      await api.markAttendance(entries);
      // Fire absence/late alerts
      const absentIds = marked.filter((s) => s.status === "absent" || s.status === "late").map((s) => s.id);
      if (absentIds.length > 0) {
        const { data } = await api.alertParents({ student_ids: absentIds, status: "absent-or-late", date });
        toast.success(`Marked ${entries.length} · alerted ${data.queued} parent(s)`);
      } else {
        toast.success(`Marked ${entries.length} students`);
      }
      api.attendanceAnalytics().then(({ data }) => setAnalytics(data));
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  const saveBoarding = async () => {
    setBusy(true);
    try {
      const entries = boarders.filter((s) => s.status).map((s) => ({ date, student_id: s.id, status: s.status }));
      await api.markBoarding(entries);
      toast.success(`Boarding roll saved · ${entries.length} students`);
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };

  const markAllPresent = () => setRoster((prev) => prev.map((s) => ({ ...s, status: s.status || "present" })));
  const markAllInHouse = () => setBoarders((prev) => prev.map((s) => ({ ...s, status: s.status || "in_house" })));

  const generateBriefing = async () => {
    setBusy(true);
    try { const { data } = await api.attendanceAiBriefing(); setBriefing(data.briefing); }
    catch { toast.error("Briefing failed"); }
    setBusy(false);
  };

  const anyMarked = useMemo(() => roster.some((s) => s.status), [roster]);
  const counts = useMemo(() => {
    const c = {}; roster.forEach((s) => { if (s.status) c[s.status] = (c[s.status] || 0) + 1; }); return c;
  }, [roster]);

  const TABS = [
    { v: "rollcall", label: "Live Roll Call", icon: ClipboardCheck },
    { v: "boarding", label: "Boarding Roll", icon: BedDouble },
    { v: "transport", label: "Transport Manifest", icon: Bus },
    { v: "analytics", label: "Analytics", icon: Sparkles },
    { v: "warnings", label: "Early Warnings", icon: AlertTriangle },
  ];

  return (
    <div data-testid="attendance-page">
      <PageHeader
        kicker="Attendance360™"
        title="Live Roll Call · Boarding · Transport · Intelligence"
        subtitle="Mobile-friendly one-touch marking across class, boarding house and school buses. Absent & late children automatically alert their parents via the distribution outbox."
      />

      <div className="flex gap-2 mb-6 overflow-x-auto -mx-2 px-2 pb-1" data-testid="att-tabs">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)} data-testid={`att-tab-${t.v}`}
            className={`whitespace-nowrap px-3 py-2 rounded-md text-xs uppercase tracking-widest border inline-flex items-center gap-1.5 ${tab === t.v ? "bg-[color:var(--hl-navy)] text-white border-[color:var(--hl-navy)]" : "bg-white text-slate-600 border-slate-200 hover:border-[color:var(--hl-orange)]"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "rollcall" && (
        <>
          <div className="hl-card p-4 mb-6 flex flex-wrap gap-3 items-end" data-testid="rc-controls">
            <div>
              <label className="overline block mb-1.5">Class</label>
              <select value={classId} onChange={(e) => setClassId(e.target.value)} data-testid="rc-class"
                className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white">
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-1.5">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="rc-date"
                className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" />
            </div>
            <div className="ml-auto flex flex-wrap gap-2">
              <button onClick={markAllPresent} data-testid="rc-all-present" className="hl-btn-primary text-sm inline-flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> All Present
              </button>
              <button onClick={saveClassRoll} disabled={busy} data-testid="rc-save" className="hl-btn-accent text-sm inline-flex items-center gap-1 disabled:opacity-40">
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save + Alert Parents"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
            {STATUSES.map((x) => (
              <div key={x.v} className="hl-card p-2.5 text-center" data-testid={`rc-count-${x.v}`}>
                <div className="overline">{x.label}</div>
                <div className="font-display font-bold text-xl">
                  {anyMarked ? (counts[x.v] || 0) : <span className="text-slate-300">—</span>}
                </div>
              </div>
            ))}
          </div>
          {!anyMarked && roster.length > 0 && (
            <div className="mb-3 text-xs text-slate-500 flex items-center gap-2" data-testid="rc-empty-notice">
              <ClipboardCheck className="w-3.5 h-3.5" />
              Awaiting roll call for {roster.length} students. Tap a status against any student to begin.
            </div>
          )}

          <div className="space-y-2" data-testid="rc-roster">
            {roster.length === 0 && <div className="text-center text-slate-500 py-8">Select a class to load the roster.</div>}
            {roster.map((s) => <RosterRow key={s.id} s={s} statuses={STATUSES} onMark={mark} testPrefix="rc" />)}
          </div>
        </>
      )}

      {tab === "boarding" && (
        <>
          <div className="hl-card p-4 mb-6 flex flex-wrap gap-3 items-end">
            <div>
              <label className="overline block mb-1.5">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="br-date"
                className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" />
            </div>
            <div className="ml-auto flex gap-2">
              <button onClick={markAllInHouse} data-testid="br-all-in-house" className="hl-btn-primary text-sm inline-flex items-center gap-1">
                <BedDouble className="w-4 h-4" /> All In House
              </button>
              <button onClick={saveBoarding} disabled={busy} data-testid="br-save" className="hl-btn-accent text-sm inline-flex items-center gap-1 disabled:opacity-40">
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save Boarding Roll"}
              </button>
            </div>
          </div>
          <div className="grid gap-2 md:grid-cols-2" data-testid="br-roster">
            {boarders.length === 0 && <div className="col-span-2 text-center text-slate-500 py-8">Loading boarders…</div>}
            {boarders.map((s) => <RosterRow key={s.id} s={s} statuses={BOARDING_STATUSES} onMark={markBoarder} testPrefix="br" />)}
          </div>
        </>
      )}

      {tab === "transport" && (
        <div className="space-y-6" data-testid="tr-manifest">
          {transport.length === 0 && <div className="text-center text-slate-500 py-8">Loading routes…</div>}
          {transport.map((r, idx) => (
            <Section key={r.route.id} title={r.route.name} subtitle={`Vehicle ${r.route.vehicle} · Driver ${r.route.driver_name} · ${r.manifest.length} students`}>
              <div className="grid gap-2 md:grid-cols-2">
                {r.manifest.map((s) => (
                  <RosterRow
                    key={s.id}
                    s={s}
                    statuses={TRANSPORT_STATUSES}
                    onMark={(id, status) => markTransport(idx, id, status)}
                    testPrefix={`tr-${idx}`}
                  />
                ))}
              </div>
            </Section>
          ))}
        </div>
      )}

      {tab === "analytics" && analytics && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KPITile tone="dark" testId="an-overall" label="Overall Attendance" value={`${analytics.overall_pct}%`} />
            <KPITile testId="an-warnings" label="Early Warnings" value={analytics.warning_count} accent={analytics.warning_count > 0 ? "monitor" : "clear"} />
            <KPITile testId="an-years" label="Year Groups" value={(analytics.by_year || []).length} />
            <KPITile tone="dark" testId="an-houses" label="Houses" value={(analytics.by_house || []).length} />
          </div>
          <div className="mb-6">
            <button onClick={generateBriefing} disabled={busy} data-testid="an-briefing" className="hl-btn-accent inline-flex items-center gap-2 text-sm disabled:opacity-40">
              <Sparkles className="w-4 h-4" /> {busy ? "Generating…" : "Generate AI Briefing"}
            </button>
          </div>
          {briefing && (
            <Section title="AI Executive Attendance Briefing">
              <div className="hl-card hl-ai-card p-5 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{briefing}</div>
            </Section>
          )}
          <Section title="By Year Group">
            <DataTable testId="an-by-year"
              columns={[
                { key: "year", label: "Year Group" },
                { key: "attendance_pct", label: "Attendance %", render: (r) => (
                  <div className="flex items-center gap-2 min-w-[200px]">
                    <div className="edu360-progress-track flex-1"><div className="edu360-progress-fill" style={{ width: `${r.attendance_pct}%` }} /></div>
                    <span className="font-semibold mono text-xs">{r.attendance_pct}%</span>
                  </div>
                ) },
                { key: "present", label: "Present" },
                { key: "total", label: "Records" },
              ]}
              rows={analytics.by_year} />
          </Section>
          <Section title="By House">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {analytics.by_house.map((h) => (
                <div key={h.house} className="hl-card p-4">
                  <div className="overline">{h.house}</div>
                  <div className="font-display font-bold text-3xl mt-1">{h.attendance_pct}%</div>
                  <div className="edu360-progress-track mt-2"><div className="edu360-progress-fill" style={{ width: `${h.attendance_pct}%` }} /></div>
                </div>
              ))}
            </div>
          </Section>
        </>
      )}

      {tab === "warnings" && analytics && (
        <Section title="Early Warning Register" subtitle="Students below 90% termly attendance">
          <DataTable testId="an-warnings-table"
            columns={[
              { key: "student_name", label: "Student" },
              { key: "year_group", label: "Year" },
              { key: "house", label: "House" },
              { key: "attendance_pct", label: "Attendance", render: (r) => <span className="mono font-semibold">{r.attendance_pct}%</span> },
              { key: "absences", label: "Absences" },
              { key: "lates", label: "Lates" },
              { key: "risk", label: "Risk", render: (r) => (
                <span className={`status-pill ${r.risk === "critical" ? "status-danger" : "status-warning"}`}>
                  <AlertTriangle className="w-3 h-3 inline mr-1" /> {r.risk}
                </span>
              ) },
            ]}
            rows={analytics.early_warnings}
            empty="No students below the 90% warning threshold." />
        </Section>
      )}
    </div>
  );
}
